import React from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Radar, ShieldOff, Bug, RotateCcw } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/ServiceSections";

const PHASES = [
  {
    key: "detect",
    icon: Radar,
    title: "Detect",
    target: "Minutes, not months",
    copy: "Telemetry from endpoints, identity, cloud and network is correlated against MITRE ATT&CK-mapped detections. Every escalated alert is triaged by a human analyst, not just a rule.",
    signals: ["Correlated cross-domain alerting", "Threat-intel enrichment", "Analyst triage with severity call"],
  },
  {
    key: "contain",
    icon: ShieldOff,
    title: "Contain",
    target: "Automated first response",
    copy: "Playbooks isolate the host, disable the identity and block the indicator while the investigation continues, so the attacker's next move is already blocked.",
    signals: ["Host isolation & account disable", "Indicator blocking at the edge", "Stakeholder comms initiated"],
  },
  {
    key: "eradicate",
    icon: Bug,
    title: "Eradicate",
    target: "Root cause, not symptoms",
    copy: "We trace the full attack path, remove persistence, close the initial access vector and confirm no secondary footholds remain anywhere in the estate.",
    signals: ["Persistence & backdoor removal", "Initial access vector closed", "Estate-wide IOC sweep"],
  },
  {
    key: "recover",
    icon: RotateCcw,
    title: "Recover",
    target: "Evidence your auditors accept",
    copy: "Systems are restored and validated, detections are tuned from what we learned, and you receive an incident report and evidence pack mapped to your framework controls.",
    signals: ["Validated restoration", "New detections deployed", "Regulator-ready incident report"],
  },
];

export const AttackLifecycle = () => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { margin: "-25%" });
  const [active, setActive] = React.useState(0);
  const [auto, setAuto] = React.useState(true);

  React.useEffect(() => {
    if (!inView || !auto) return;
    const id = setInterval(() => setActive((a) => (a + 1) % PHASES.length), 4200);
    return () => clearInterval(id);
  }, [inView, auto]);

  const phase = PHASES[active];

  return (
    <section
      ref={ref}
      className="relative overflow-hidden border-y border-white/10 bg-[#0A0E19]"
      data-testid="attack-lifecycle"
    >
      <div className="glow-orb -right-32 top-0 h-[420px] w-[420px] bg-[#4C5A8C]/20" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24 lg:px-16 lg:py-28">
        <Reveal>
          <Eyebrow>Incident Response Lifecycle</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.75rem] lg:leading-[1.12]">
            What happens in the <span className="text-[#8E9AEA]">first hour</span> of an incident.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#DDE3F2]">
            Detection without response is just a notification. This is the sequence our SOC runs on every confirmed
            incident, and the artifacts you get at each step.
          </p>
        </Reveal>

        <div className="mt-10 sm:mt-14 grid gap-8 lg:grid-cols-[1fr_1.05fr] lg:gap-14">
          <div className="flex flex-col gap-3">
            {PHASES.map((p, i) => {
              const isActive = i === active;
              return (
                <button
                  key={p.key}
                  onMouseEnter={() => {
                    setAuto(false);
                    setActive(i);
                  }}
                  onFocus={() => {
                    setAuto(false);
                    setActive(i);
                  }}
                  onClick={() => {
                    setAuto(false);
                    setActive(i);
                  }}
                  className={`relative overflow-hidden rounded-xl border px-5 py-4 text-left transition-colors duration-300 sm:py-5 ${
                    isActive
                      ? "border-[#8E9AEA]/50 bg-[#8E9AEA]/[0.08]"
                      : "border-white/10 bg-white/[0.02] hover:border-white/25"
                  }`}
                  data-testid={`lifecycle-step-${p.key}`}
                >
                  <div className="flex items-center gap-4">
                    <span
                      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors duration-300 ${
                        isActive ? "border-[#8E9AEA]/50 bg-[#8E9AEA]/15" : "border-white/10 bg-white/[0.04]"
                      }`}
                    >
                      <p.icon className={`h-5 w-5 ${isActive ? "text-[#8E9AEA]" : "text-[#A6B0C9]"}`} />
                    </span>
                    <div className="min-w-0">
                      <p className="font-display text-lg font-bold text-white">
                        <span className="mr-3 text-xs tracking-[0.3em] text-[#8E9AEA]">0{i + 1}</span>
                        {p.title}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-[#A6B0C9]">{p.target}</p>
                    </div>
                  </div>
                  {isActive && (
                    <motion.span
                      key={`${p.key}-bar-${auto}`}
                      className="absolute bottom-0 left-0 h-0.5 bg-[#8E9AEA]"
                      initial={{ width: 0 }}
                      animate={{ width: auto ? "100%" : "100%" }}
                      transition={{ duration: auto ? 4.2 : 0.5, ease: "linear" }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          <div className="glass-card relative min-h-[300px] overflow-hidden p-5 sm:min-h-[340px] sm:p-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={phase.key}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`lifecycle-panel-${phase.key}`}
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#8E9AEA]">
                  Phase 0{active + 1} · {phase.title}
                </p>
                <h3 className="mt-4 font-display text-2xl font-bold text-white sm:text-[1.75rem]">{phase.target}</h3>
                <p className="mt-5 text-sm leading-relaxed text-[#DDE3F2] sm:text-base">{phase.copy}</p>
                <ul className="mt-8 flex flex-col gap-3">
                  {phase.signals.map((s, i) => (
                    <motion.li
                      key={s}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.1, duration: 0.45 }}
                      className="flex items-center gap-3 rounded-lg border border-white/10 bg-[#0B0F1A]/60 px-4 py-3 text-sm text-[#DDE3F2]"
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#8E9AEA]" />
                      {s}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
